import { useEffect, useState, useSyncExternalStore } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { pendingChunkLoads, subscribeChunkLoads } from '@/routing/lazy-page';

/**
 * A slim bar across the top of the viewport while the app is waiting on the server.
 *
 * The scaffolding animates it on every route change for a fixed 400 ms, whether or not anything loads. Here
 * it tracks the real thing: it shows while a query or a mutation is in flight — except the inbox's own
 * fifteen-second poll, which would otherwise flash it all day — or while a screen's own chunk is on the way,
 * and only after 150 ms, so an instant answer shows nothing. It is decoration (`aria-hidden`): every screen
 * already says "Đang tải…" in words.
 *
 * The keyframes live in app-tokens.css; nothing is injected at runtime (production CSP is `style-src 'self'`).
 * The track clips its own overflow, so the sliding segment never widens the document.
 */
export function TopLoadingBar() {
  const fetching = useIsFetching({ predicate: (query) => query.queryKey[0] !== 'notifications' });
  const mutating = useIsMutating();
  const chunks = useSyncExternalStore(subscribeChunkLoads, pendingChunkLoads);
  const busy = fetching + mutating + chunks > 0;
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!busy) {
      setShown(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShown(true), 150);
    return () => window.clearTimeout(timer);
  }, [busy]);

  if (!shown) return null;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-[70] h-[3px] overflow-hidden bg-[hsl(var(--h-primary)/0.15)]"
    >
      <div className="h-loading-bar h-full w-2/5 animate-[h-loading-slide_1.1s_ease-in-out_infinite] bg-[hsl(var(--h-primary))]" />
    </div>
  );
}
